import { useMemo } from 'react';

import { useGetEntities } from 'pages/UserActions/hooks/useGetEntities';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useGetList, useListContext } from 'react-admin';
import { Controller, useFormContext } from 'react-hook-form';
import { DateTimeRangePicker } from 'shared/mui/DatePicker/DateTimeRangePicker/DateTimeRangePicker';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { AutocompleteMultiselectInput, TextInput } from 'shared/react-admin/Inputs';
import { ReferenceArrayInput } from 'shared/react-admin/ReferenceInputs';
import { User } from 'types/embedded/user';
import { UserEventType } from 'types/embedded/userAction';

export const useUserActionsListFilterFields = (): FilterFieldConfig[] => {
  const translate = useTypedTranslate();

  const { control } = useFormContext();
  const { filterValues } = useListContext();

  const { data: eventTypes } = useGetList<UserEventType>('user_event_types');
  const { data: entitiesList } = useGetEntities();
  const entities = Object.entries(entitiesList?.data || [])?.map(([key, value]) => ({
    id: key,
    name: value,
  }));
  const defaultEntity = useMemo(
    () =>
      filterValues?.entityName &&
      entities?.filter(({ id }) => filterValues?.entityName?.includes(id)),
    [entities, filterValues],
  );
  const defaultEvent = useMemo(
    () =>
      filterValues?.eventType &&
      eventTypes?.filter(({ value }) => filterValues?.eventType.includes(value)),
    [filterValues, eventTypes],
  );

  return [
    {
      name: 'appliedDate',
      label: translate('app.filter.creationTime'),
      chipType: 'dateTimeRange',
      input: (
        <Controller
          control={control}
          name="appliedDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <DateTimeRangePicker
              label={translate('app.filter.period')}
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
            />
          )}
        />
      ),
    },
    {
      name: 'entityName',
      label: translate('app.filter.entities'),
      chipType: 'multiselect',
      chipProps: {
        resource: 'entities',
        limitTags: 1,
        options: entities,
      },
      input: (
        <AutocompleteMultiselectInput
          defaultValue={defaultEntity}
          filterToQuery={(searchText: string) => ({ name: searchText })}
          helperText={false}
          label={translate('app.filter.entities')}
          name="entityName"
          optionText="name"
          optionValue="id"
          options={entities || []}
        />
      ),
    },
    {
      name: 'entityId',
      label: translate('app.filter.entityId'),
      chipType: 'text',
      input: (
        <TextInput
          fullWidth
          helperText={false}
          label={translate('app.filter.entityId')}
          name="entityId"
          resettable
          source="entityId"
          variant="outlined"
        />
      ),
    },
    {
      name: 'roleEntities',
      label: translate('app.filter.roles'),
      chipType: 'multiselect',
      chipProps: {
        resource: 'roles',
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput perPage={200} reference="roles" source="roleEntities">
          <AutocompleteMultiselectInput
            helperText={false}
            label={translate('app.filter.roles')}
            name="roleEntities"
            optionText="name"
            size="small"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'user',
      label: translate('app.filter.users'),
      chipType: 'multiselect',
      chipProps: {
        resource: 'users',
        limitTags: 1,
        renderChipLabel: (choice: User) => `${choice.firstName} ${choice.lastName}`,
      },
      input: (
        <ReferenceArrayInput perPage={200} reference="users" source="user">
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({
              fullName: searchText,
            })}
            getOptionLabel={(choice: User) => `${choice.firstName} ${choice.lastName}`}
            helperText={false}
            label={translate('app.filter.users')}
            name="user"
            size="small"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'eventType',
      label: translate('app.filter.eventTypes'),
      chipType: 'multiselect',
      chipProps: {
        resource: 'user_event_types',
        limitTags: 1,
        options: eventTypes || [],
        optionText: 'description',
        optionValue: 'value',
      },
      input: (
        <AutocompleteMultiselectInput
          defaultValue={defaultEvent}
          getOptionLabel={({ description }) => description}
          helperText={false}
          label={translate('app.filter.eventTypes')}
          name="eventType"
          optionValue="value"
          options={eventTypes || []}
          size="small"
        />
      ),
    },
  ];
};
